import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Card,
  CardContent,
  Divider,
  Checkbox,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  CardHeader,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import { useNotification } from "config/Notification";
import {
  fetchCompanyStandardsByCompany as fetchCompanyStandardsByCompanyService,
  saveNewQuestionByCompanyStandardControl as saveNewQuestionByCompanyStandardControlService,
  fetchControlsByCompanyStandardId as fetchControlsByCompanyStandardIdService,
  fetchSubControlsByCompanyStandardControl as fetchSubControlsByCompanyStandardControlService,
  updateCompanySubcontrolsById as updateCompanySubControlsByIdService,
} from "services/modules/cap/standardservice";
import Breadcrumbs from "config/Breadcrumb";

// US-CAP-103: QuestionRenderer for live preview
import QuestionRenderer, {
  QUESTION_TYPE_LABELS,
} from "../shared/QuestionRenderer";

// US-CAP-103: Valid question types
const VALID_QUESTION_TYPES = [
  "five_option",
  "yes_no",
  "checkbox",
  "radio",
  "text_box",
];

// US-CAP-103: Default answer_config per type
const getDefaultAnswerConfig = (type) => {
  switch (type) {
    case "five_option":
      return null;
    case "yes_no":
      return { labels: { yes: "Yes", no: "No", na: "N/A" } };
    case "checkbox":
      return { options: ["Option 1", "Option 2"], minSelect: 1, maxSelect: 2 };
    case "radio":
      return { options: ["Option 1", "Option 2", "Option 3"] };
    case "text_box":
      return { placeholder: "", reviewerInstructions: "", maxLength: 2000 };
    default:
      return null;
  }
};

const ConfigureQuestions = () => {
  const standardTypes = [{ id: "domain-based", name: "Domain Based" }];

  // React router
  const navigate = useNavigate();

  // Notification
  const { openNotification } = useNotification();

  // State Variables
  const [standardType, setStandardType] = useState(standardTypes[0].id);
  const [companyId, setCompanyId] = useState(null);
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [controls, setControls] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [activeSubcontrol, setActiveSubcontrol] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isStep3Editing, setIsStep3Editing] = useState(false);
  const [editQuestionId, setEditQuestionId] = useState(null);
  const [openStep3, setOpenStep3] = useState(false);

  // Redux selectors
  const selectedCompanyContainer = useSelector(
    (state) => state.auth.companyData,
  );

  // Fetch standards data based on the selected standard type
  const fetchStandardsData = async () => {
    try {
      let group =
        standardType === "standard-based" ? "Main Standard" : "Domain Standard";
      const response = await fetchCompanyStandardsByCompanyService({
        group,
        companyId,
      });
      setSelectedStandards(response.data);
    } catch (error) {
      openNotification("Error while fetching data", "error");
    }
  };

  // Fetch controls based on the selected standard
  const fetchControlsDataByStandard = async (standard) => {
    try {
      const params = { Standard: standard, company: companyId };
      const response = await fetchControlsByCompanyStandardIdService(params);
      setControls(response.data || []);
    } catch (error) {
      openNotification("Error while fetching controls", "error");
    }
  };

  // Fetch sub-controls based on the selected control
  const fetchSubControlsDataByControl = async (standardId, controlId) => {
    try {
      const response = await fetchSubControlsByCompanyStandardControlService({
        standardId,
        controlId,
        companyId,
      });
      setQuestions(response.data || []);
    } catch (error) {
      openNotification("Error while fetching sub-controls", "error");
    }
  };

  // This method handles to change the pages
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // This method handles to change the rows per page based on user selection
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const validationSchemaStep3 = Yup.object({
    standard: Yup.string().required("Required"),
    control: Yup.string().required("Required"),
    subControlId: Yup.string(),
    subControlName: Yup.string(),
    slno: Yup.string().required("Required"),
    // US-CAP-103
    question_type: Yup.string()
      .oneOf(VALID_QUESTION_TYPES)
      .required("Required"),
  });

  // Formik setup for sub controls and questions
  const formikStep3 = useFormik({
    initialValues: {
      standardName: "",
      standard: "",
      control: "",
      subControlId: "",
      subControlName: "",
      slno: "",
      questionId: "",
      questionName: "",
      questionDescription: "",
      status: true,
      // US-CAP-103
      question_type: "five_option",
      answer_config: null,
      // US-CAP-206: top-level informational flag
      informational: false,
      // US-CAP-302: top-level evidence-mandatory flag
      evidence_mandatory: false,
    },
    validationSchema: validationSchemaStep3,
    onSubmit: async (values) => {
      try {
        const body = {
          Standard: values.standardName,
          ControlID: values.control,
          SubControlID: values.subControlId,
          slno: values.slno,
          SubControlName: values.subControlName,
          Question_ID: values.questionId,
          Question: values.questionName,
          Description: values.questionDescription,
          company: companyId,
          status: values.status,
          // US-CAP-103
          question_type: values.question_type,
          answer_config: values.answer_config,
          // US-CAP-206
          informational: values.informational,
          // US-CAP-302
          evidence_mandatory: values.evidence_mandatory,
        };
        let standard = values.standard;
        let standardName = values.standardName;
        let control = values.control;
        if (isStep3Editing) {
          let subcontrolId = editQuestionId;
          let standarId = values.standard;
          setActiveSubcontrol(false);
          const response = await updateCompanySubControlsByIdService(
            subcontrolId,
            standarId,
            { body },
          );
          if (response?.status === 409) {
            openNotification(response.data.message || "Conflict", "error");
            return;
          }
          if (response.status === 200) {
            openNotification("Question updated successfully", "success");
          }
        } else {
          const response = await saveNewQuestionByCompanyStandardControlService(
            { standard, body },
          );
          if (response?.status === 409) {
            openNotification(response.data.message || "Conflict", "error");
            return;
          }
          if (response.status === 201) {
            setActiveSubcontrol(true);
            openNotification("Question saved successfully", "success");
          }
        }

        fetchSubControlsDataByControl(
          formikStep3.values.standardName,
          formikStep3.values.control,
        );
        formikStep3.resetForm();
        formikStep3.setFieldValue("standardName", standardName);
        formikStep3.setFieldValue("standard", standard);
        formikStep3.setFieldValue("control", control);
        formikStep3.setFieldValue("question_type", "five_option");
        formikStep3.setFieldValue("answer_config", null);
        formikStep3.setFieldValue("informational", false);
        formikStep3.setFieldValue("evidence_mandatory", false);
        setIsStep3Editing(false);
      } catch (error) {
        if (error.response?.status === 409) {
          openNotification(error.response.data.message || "Conflict", "error");
        } else {
          openNotification("Error while saving question", "error");
        }
      }
    },
  });

  // Handles to edit the sub control and question details
  const handleEditQuestionClick = (question) => {
    setIsStep3Editing(true);
    setEditQuestionId(question.id);
    formikStep3.setFieldValue("subControlId", question.SubControlID);
    formikStep3.setFieldValue("subControlName", question.SubControlName);
    formikStep3.setFieldValue("slno", question.slno);
    formikStep3.setFieldValue("questionId", question.Question_ID);
    formikStep3.setFieldValue("questionName", question.Question);
    formikStep3.setFieldValue(
      "questionDescription",
      question.Description ?? "",
    );
    formikStep3.setFieldValue("status", question.status);
    // US-CAP-103: restore question_type and answer_config
    formikStep3.setFieldValue(
      "question_type",
      question.question_type || "five_option",
    );
    formikStep3.setFieldValue(
      "answer_config",
      question.answer_config ||
        getDefaultAnswerConfig(question.question_type || "five_option"),
    );
    // US-CAP-206: restore informational flag
    formikStep3.setFieldValue("informational", question.informational === true);
    // US-CAP-302: restore evidence_mandatory flag
    formikStep3.setFieldValue(
      "evidence_mandatory",
      question.evidence_mandatory === true,
    );
  };

  // Handles to change the status of sub control
  const handleStep3StatusChange = (event) => {
    const newStatus = event.target.value === "true";
    if (!newStatus) {
      setOpenStep3(true);
    } else {
      formikStep3.setFieldValue("status", true);
    }
  };

  // Hanldes to confirm the status to inactive
  const handleStep3ConfirmInactive = () => {
    formikStep3.setFieldValue("status", false);
    setOpenStep3(false);
  };

  // Handles to change the selected company
  useEffect(() => {
    if (selectedCompanyContainer) {
      setCompanyId(selectedCompanyContainer.companyid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyContainer]);

  // Handles to reset the form when company or standard changes
  useEffect(() => {
    if (companyId) {
      fetchStandardsData();
      formikStep3.resetForm();
      setIsStep3Editing(false);
      setQuestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standardType, companyId]);

  useEffect(() => {
    if (formikStep3.values.standard) {
      const selectedStandard = selectedStandards.find(
        (standard) => standard.id === formikStep3.values.standard,
      );

      if (selectedStandard) {
        formikStep3.setFieldValue("standardName", selectedStandard.stdid);
      }
      fetchControlsDataByStandard(selectedStandard.stdid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikStep3.values.standard]);

  useEffect(() => {
    if (formikStep3.values.standard && formikStep3.values.control) {
      fetchSubControlsDataByControl(
        formikStep3.values.standardName,
        formikStep3.values.control,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikStep3.values.standard, formikStep3.values.control]);

  useEffect(() => {
    if (questions.length > 0) {
      formikStep3.setFieldValue("slno", questions.length + 1);
    } else {
      formikStep3.setFieldValue("slno", 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  return (
    <div className="main-cont">
      <Card>
        <CardHeader
          title={
            <React.Fragment>
              <div className="card-head">
                <div className="flex-head">
                  <label className="label">
                    <Breadcrumbs
                      paths={["CAP Configurator", "Configure Questions"]}
                    />
                  </label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    className="back font"
                    onClick={() => window.history.back()}
                  >
                    Back
                  </Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
        <CardContent>
          <FormControl component="fieldset">
            <FormLabel component="legend">Select Standard Type</FormLabel>
            <RadioGroup
              aria-label="standard-type"
              name="standardType"
              value={standardType}
              onChange={(e) => setStandardType(e.target.value)}
              className="d-flex flex-row gap-2"
            >
              {standardTypes.map((type) => (
                <FormControlLabel
                  key={type.id}
                  value={type.id}
                  control={<Radio />}
                  label={type.name}
                />
              ))}
            </RadioGroup>
          </FormControl>
          <Divider className="my-1" />
          {standardType && (
            <>
              <CardContent className="loader-container">
                <div className="d-flex justify-content-center mb-4">
                  <Card className="row company-screen-bg col-md-10">
                    <CardContent>
                      <form onSubmit={formikStep3.handleSubmit}>
                        <div className="row ">
                          <FormControl
                            className="col-md-6 px-2"
                            margin="normal"
                          >
                            <InputLabel htmlFor="standard">Standard</InputLabel>
                            <Select
                              name="standard"
                              label="Standard"
                              value={formikStep3.values.standard}
                              onChange={formikStep3.handleChange}
                              error={
                                formikStep3.touched.standard &&
                                Boolean(formikStep3.errors.standard)
                              }
                            >
                              {selectedStandards &&
                                selectedStandards.map((standard) => (
                                  <MenuItem
                                    key={standard.id}
                                    value={standard.id}
                                  >
                                    {standard.name}
                                  </MenuItem>
                                ))}
                            </Select>
                            {formikStep3.touched.standard &&
                              formikStep3.errors.standard && (
                                <div className="error-message">
                                  {formikStep3.errors.standard}
                                </div>
                              )}
                          </FormControl>
                          <FormControl
                            className="col-md-6 px-2"
                            margin="normal"
                          >
                            <InputLabel htmlFor="control">Control</InputLabel>
                            <Select
                              name="control"
                              label="Control"
                              value={formikStep3.values.control}
                              onChange={formikStep3.handleChange}
                              error={
                                formikStep3.touched.control &&
                                Boolean(formikStep3.errors.control)
                              }
                            >
                              {controls.map((control) => (
                                <MenuItem
                                  key={control.Control}
                                  value={control.ControlID}
                                >
                                  {control.Title}
                                </MenuItem>
                              ))}
                            </Select>
                            {formikStep3.touched.control &&
                              formikStep3.errors.control && (
                                <div className="error-message">
                                  {formikStep3.errors.control}
                                </div>
                              )}
                          </FormControl>
                        </div>
                        <div className="row ">
                          <FormControl
                            className="col-md-6 px-2"
                            margin="normal"
                          >
                            <TextField
                              label="SubControl ID"
                              name="subControlId"
                              disabled={standardType === "standard-based"}
                              onChange={formikStep3.handleChange}
                              value={formikStep3.values.subControlId}
                              error={
                                formikStep3.touched.subControlId &&
                                Boolean(formikStep3.errors.subControlId)
                              }
                              helperText={
                                formikStep3.touched.subControlId &&
                                formikStep3.errors.subControlId
                              }
                            />
                          </FormControl>

                          <FormControl
                            className="col-md-6 px-2"
                            margin="normal"
                          >
                            <TextField
                              label="Sub-Control"
                              name="subControlName"
                              disabled={standardType === "standard-based"}
                              onChange={formikStep3.handleChange}
                              value={formikStep3.values.subControlName}
                              error={
                                formikStep3.touched.subControlName &&
                                Boolean(formikStep3.errors.subControlName)
                              }
                              helperText={
                                formikStep3.touched.subControlName &&
                                formikStep3.errors.subControlName
                              }
                            />
                          </FormControl>
                        </div>
                        <div className="row ">
                          <FormControl
                            className="col-md-6 px-2"
                            margin="normal"
                          >
                            <TextField
                              label="Question Id"
                              name="questionId"
                              disabled={standardType === "standard-based"}
                              onChange={formikStep3.handleChange}
                              value={formikStep3.values.questionId}
                              error={
                                formikStep3.touched.questionId &&
                                Boolean(formikStep3.errors.questionId)
                              }
                              helperText={
                                formikStep3.touched.questionId &&
                                formikStep3.errors.questionId
                              }
                            />
                          </FormControl>

                          <FormControl
                            className="col-md-6 px-2"
                            margin="normal"
                          >
                            <TextField
                              label="Question"
                              name="questionName"
                              disabled={standardType === "standard-based"}
                              onChange={formikStep3.handleChange}
                              value={formikStep3.values.questionName}
                              error={
                                formikStep3.touched.questionName &&
                                Boolean(formikStep3.errors.questionName)
                              }
                              helperText={
                                formikStep3.touched.questionName &&
                                formikStep3.errors.questionName
                              }
                            />
                          </FormControl>
                        </div>
                        <FormControl fullWidth margin="normal">
                          <TextField
                            label="Description"
                            name="questionDescription"
                            disabled={standardType === "standard-based"}
                            onChange={formikStep3.handleChange}
                            value={formikStep3.values.questionDescription}
                            error={
                              formikStep3.touched.questionDescription &&
                              Boolean(formikStep3.errors.questionDescription)
                            }
                            helperText={
                              formikStep3.touched.questionDescription &&
                              formikStep3.errors.questionDescription
                            }
                            multiline
                            rows={1}
                            maxRows={Infinity}
                          />
                        </FormControl>

                        {/* US-CAP-103: Question Type Selector + Answer Config Panel + Live Preview */}
                        <div className="row mt-2">
                          <FormControl
                            className="col-md-6 px-2"
                            margin="normal"
                          >
                            <InputLabel htmlFor="question_type">
                              Question Type
                            </InputLabel>
                            <Select
                              name="question_type"
                              label="Question Type"
                              value={formikStep3.values.question_type}
                              onChange={(e) => {
                                const newType = e.target.value;
                                formikStep3.setFieldValue(
                                  "question_type",
                                  newType,
                                );
                                formikStep3.setFieldValue(
                                  "answer_config",
                                  getDefaultAnswerConfig(newType),
                                );
                              }}
                              disabled={standardType === "standard-based"}
                            >
                              {VALID_QUESTION_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>
                                  {QUESTION_TYPE_LABELS[t]}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <div className="col-md-6 px-2 d-flex align-items-center">
                            <Chip
                              label={`Type: ${
                                QUESTION_TYPE_LABELS[
                                  formikStep3.values.question_type
                                ] || formikStep3.values.question_type
                              }`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </div>
                        </div>

                        {/* Answer Config Panel — conditional per type */}
                        {formikStep3.values.question_type !== "five_option" && (
                          <div
                            className="row mt-2 p-2"
                            style={{
                              background: "#f5f7fa",
                              borderRadius: 6,
                              margin: "8px 0",
                            }}
                          >
                            <div className="col-md-12 px-2">
                              <strong style={{ fontSize: 13 }}>
                                Answer Configuration
                              </strong>
                            </div>

                            {/* yes_no labels */}
                            {formikStep3.values.question_type === "yes_no" && (
                              <div className="row">
                                {["yes", "no", "na"].map((key) => (
                                  <FormControl
                                    key={key}
                                    className="col-md-4 px-2"
                                    margin="dense"
                                  >
                                    <TextField
                                      label={`Label for "${key.toUpperCase()}"`}
                                      size="small"
                                      value={
                                        formikStep3.values.answer_config
                                          ?.labels?.[key] || ""
                                      }
                                      onChange={(e) => {
                                        const updated = {
                                          ...formikStep3.values.answer_config,
                                          labels: {
                                            ...formikStep3.values.answer_config
                                              ?.labels,
                                            [key]: e.target.value,
                                          },
                                        };
                                        formikStep3.setFieldValue(
                                          "answer_config",
                                          updated,
                                        );
                                      }}
                                    />
                                  </FormControl>
                                ))}
                              </div>
                            )}

                            {/* checkbox + radio: dynamic options list */}
                            {(formikStep3.values.question_type === "checkbox" ||
                              formikStep3.values.question_type === "radio") && (
                              <div className="col-md-12">
                                <div
                                  style={{
                                    fontSize: 12,
                                    marginBottom: 4,
                                    color: "#555",
                                  }}
                                >
                                  Options (min 2)
                                </div>
                                {(
                                  formikStep3.values.answer_config?.options ||
                                  []
                                ).map((opt, idx) => (
                                  <div
                                    key={idx}
                                    className="d-flex align-items-center mb-1"
                                  >
                                    <TextField
                                      size="small"
                                      value={opt}
                                      onChange={(e) => {
                                        const opts = [
                                          ...(formikStep3.values.answer_config
                                            ?.options || []),
                                        ];
                                        opts[idx] = e.target.value;
                                        formikStep3.setFieldValue(
                                          "answer_config",
                                          {
                                            ...formikStep3.values.answer_config,
                                            options: opts,
                                          },
                                        );
                                      }}
                                      style={{ flex: 1, marginRight: 4 }}
                                    />
                                    <Tooltip title="Remove option">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const opts = (
                                            formikStep3.values.answer_config
                                              ?.options || []
                                          ).filter((_, i) => i !== idx);
                                          formikStep3.setFieldValue(
                                            "answer_config",
                                            {
                                              ...formikStep3.values
                                                .answer_config,
                                              options: opts,
                                              maxSelect: Math.min(
                                                formikStep3.values.answer_config
                                                  ?.maxSelect || opts.length,
                                                opts.length,
                                              ),
                                            },
                                          );
                                        }}
                                        disabled={
                                          (
                                            formikStep3.values.answer_config
                                              ?.options || []
                                          ).length <= 2
                                        }
                                      >
                                        <RemoveCircleOutlineIcon
                                          fontSize="small"
                                          color="error"
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </div>
                                ))}
                                <Button
                                  size="small"
                                  startIcon={<AddCircleOutlineIcon />}
                                  onClick={() => {
                                    const opts = [
                                      ...(formikStep3.values.answer_config
                                        ?.options || []),
                                      "",
                                    ];
                                    formikStep3.setFieldValue("answer_config", {
                                      ...formikStep3.values.answer_config,
                                      options: opts,
                                    });
                                  }}
                                >
                                  Add Option
                                </Button>

                                {/* checkbox-only: minSelect / maxSelect */}
                                {formikStep3.values.question_type ===
                                  "checkbox" && (
                                  <div className="row mt-1">
                                    <FormControl
                                      className="col-md-6 px-2"
                                      margin="dense"
                                    >
                                      <TextField
                                        label="Min Selections"
                                        size="small"
                                        type="number"
                                        value={
                                          formikStep3.values.answer_config
                                            ?.minSelect || 1
                                        }
                                        inputProps={{
                                          min: 1,
                                          max:
                                            formikStep3.values.answer_config
                                              ?.options?.length || 2,
                                        }}
                                        onChange={(e) =>
                                          formikStep3.setFieldValue(
                                            "answer_config",
                                            {
                                              ...formikStep3.values
                                                .answer_config,
                                              minSelect:
                                                parseInt(e.target.value) || 1,
                                            },
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormControl
                                      className="col-md-6 px-2"
                                      margin="dense"
                                    >
                                      <TextField
                                        label="Max Selections"
                                        size="small"
                                        type="number"
                                        value={
                                          formikStep3.values.answer_config
                                            ?.maxSelect || 2
                                        }
                                        inputProps={{
                                          min: 1,
                                          max:
                                            formikStep3.values.answer_config
                                              ?.options?.length || 2,
                                        }}
                                        onChange={(e) =>
                                          formikStep3.setFieldValue(
                                            "answer_config",
                                            {
                                              ...formikStep3.values
                                                .answer_config,
                                              maxSelect:
                                                parseInt(e.target.value) || 2,
                                            },
                                          )
                                        }
                                      />
                                    </FormControl>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* text_box config */}
                            {formikStep3.values.question_type ===
                              "text_box" && (
                              <div className="col-md-12">
                                <FormControl fullWidth margin="dense">
                                  <TextField
                                    label="Placeholder text"
                                    size="small"
                                    value={
                                      formikStep3.values.answer_config
                                        ?.placeholder || ""
                                    }
                                    onChange={(e) =>
                                      formikStep3.setFieldValue(
                                        "answer_config",
                                        {
                                          ...formikStep3.values.answer_config,
                                          placeholder: e.target.value,
                                        },
                                      )
                                    }
                                  />
                                </FormControl>
                                <FormControl fullWidth margin="dense">
                                  <TextField
                                    label="Reviewer instructions"
                                    size="small"
                                    multiline
                                    rows={2}
                                    value={
                                      formikStep3.values.answer_config
                                        ?.reviewerInstructions || ""
                                    }
                                    onChange={(e) =>
                                      formikStep3.setFieldValue(
                                        "answer_config",
                                        {
                                          ...formikStep3.values.answer_config,
                                          reviewerInstructions: e.target.value,
                                        },
                                      )
                                    }
                                  />
                                </FormControl>
                                <FormControl
                                  className="col-md-4"
                                  margin="dense"
                                >
                                  <TextField
                                    label="Max length (chars)"
                                    size="small"
                                    type="number"
                                    value={
                                      formikStep3.values.answer_config
                                        ?.maxLength || 2000
                                    }
                                    inputProps={{ min: 100, max: 10000 }}
                                    onChange={(e) =>
                                      formikStep3.setFieldValue(
                                        "answer_config",
                                        {
                                          ...formikStep3.values.answer_config,
                                          maxLength:
                                            parseInt(e.target.value) || 2000,
                                        },
                                      )
                                    }
                                  />
                                </FormControl>
                              </div>
                            )}

                            {/* Live preview */}
                            <div
                              className="col-md-12 mt-2 pt-2"
                              style={{ borderTop: "1px solid #ddd" }}
                            >
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "#888",
                                  marginBottom: 6,
                                }}
                              >
                                Live Preview
                              </div>
                              <QuestionRenderer
                                question_type={formikStep3.values.question_type}
                                answer_config={formikStep3.values.answer_config}
                                readOnly={true}
                              />
                            </div>
                          </div>
                        )}

                        {/* US-CAP-206: Informational toggle — top-level field, applies to ALL question types (including five_option) */}
                        <div
                          className="col-md-12 mt-2 pt-2"
                          style={{ borderTop: "1px solid #ddd" }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  formikStep3.values.informational === true
                                }
                                onChange={(e) =>
                                  formikStep3.setFieldValue(
                                    "informational",
                                    e.target.checked,
                                  )
                                }
                                size="small"
                                sx={{
                                  color: "#1565C0",
                                  "&.Mui-checked": { color: "#1565C0" },
                                }}
                              />
                            }
                            label={
                              <span style={{ fontSize: 13 }}>
                                <strong>Informational only</strong>
                                <span style={{ color: "#666", marginLeft: 6 }}>
                                  — answer required but never counted in score
                                </span>
                              </span>
                            }
                          />
                          {formikStep3.values.informational && (
                            <div
                              style={{
                                padding: "5px 10px",
                                background: "#EAF4FB",
                                border: "1px solid #B3D9F5",
                                borderRadius: 4,
                                fontSize: 12,
                                color: "#1565C0",
                                marginTop: 4,
                              }}
                            >
                              ℹ️ Users will see this question marked as
                              informational. Their answer is recorded but
                              excluded from the compliance score and gap
                              analysis.
                            </div>
                          )}
                        </div>

                        {/* US-CAP-302: Evidence-mandatory toggle — top-level field, applies to ALL question types (including five_option) */}
                        <div className="col-md-12 mt-2 pt-2">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  formikStep3.values.evidence_mandatory === true
                                }
                                onChange={(e) =>
                                  formikStep3.setFieldValue(
                                    "evidence_mandatory",
                                    e.target.checked,
                                  )
                                }
                                size="small"
                                sx={{
                                  color: "#B71C1C",
                                  "&.Mui-checked": { color: "#B71C1C" },
                                }}
                              />
                            }
                            label={
                              <span style={{ fontSize: 13 }}>
                                <strong>Evidence mandatory</strong>
                                <span style={{ color: "#666", marginLeft: 6 }}>
                                  — user must attach at least one evidence file
                                  before this answer can be saved
                                </span>
                              </span>
                            }
                          />
                          {formikStep3.values.evidence_mandatory && (
                            <div
                              style={{
                                padding: "5px 10px",
                                background: "#FDECEA",
                                border: "1px solid #F5C6CB",
                                borderRadius: 4,
                                fontSize: 12,
                                color: "#B71C1C",
                                marginTop: 4,
                              }}
                            >
                              📎 On the self-assessment page, users won't be
                              able to save or submit an answer to this question
                              until they've uploaded at least one supporting
                              evidence file (max 2 MB each, multiple files
                              allowed).
                            </div>
                          )}
                        </div>

                        {isStep3Editing && (
                          <>
                            <div className="row col-md-12 px-2 align-items-center">
                              <FormControl
                                component="fieldset"
                                className="d-flex"
                                margin="normal"
                              >
                                <RadioGroup
                                  row
                                  aria-label="status"
                                  name="status"
                                  value={
                                    formikStep3.values.status ? "true" : "false"
                                  }
                                  onChange={handleStep3StatusChange}
                                >
                                  <FormControlLabel
                                    value="true"
                                    control={<Radio />}
                                    label="Active"
                                  />
                                  <FormControlLabel
                                    value="false"
                                    control={<Radio />}
                                    label="Inactive"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </div>
                          </>
                        )}
                        <div className=" d-flex justify-content-center align-items-center mt-2">
                          <Button
                            color="primary"
                            variant="contained"
                            className="mx-3 custom-width"
                            disabled={
                              !isStep3Editing &&
                              standardType === "standard-based"
                            }
                            type="submit"
                          >
                            {isStep3Editing ? "Update" : "Save"}
                          </Button>
                          <Button
                            variant="contained"
                            className="danger .custom-width"
                            disabled={
                              !isStep3Editing &&
                              standardType === "standard-based"
                            }
                            onClick={() => {
                              let standard = formikStep3.values.standard;
                              let standardName =
                                formikStep3.values.standardName;
                              let control = formikStep3.values.control;

                              formikStep3.resetForm();
                              formikStep3.setFieldValue(
                                "standardName",
                                standardName,
                              );
                              formikStep3.setFieldValue("standard", standard);
                              formikStep3.setFieldValue("control", control);
                              formikStep3.setFieldValue(
                                "slno",
                                questions.length > 0 ? questions.length + 1 : 1,
                              );
                              setIsStep3Editing(false);
                            }}
                          >
                            Cancel
                          </Button>
                          {activeSubcontrol && (
                            <Button
                              variant="contained"
                              className="mx-3"
                              style={{ maxWidth: "280px", minWidth: "70px" }}
                              onClick={() => {
                                navigate(
                                  "/cap/adm-configurecontrolsweightages",
                                );
                              }}
                            >
                              Assign Weightage
                            </Button>
                          )}
                        </div>
                        <Dialog
                          open={openStep3}
                          onClose={() => setOpenStep3(false)}
                          aria-labelledby="alert-dialog-title"
                          aria-describedby="alert-dialog-description"
                        >
                          <DialogTitle id="alert-dialog-title">
                            {"Confirm Inactive Status"}
                          </DialogTitle>
                          <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                              Would you really like to change the status to
                              "Inactive"?.
                            </DialogContentText>
                          </DialogContent>
                          <DialogActions>
                            <Button
                              onClick={() => setOpenStep3(false)}
                              color="primary"
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={handleStep3ConfirmInactive}
                              color="secondary"
                              autoFocus
                            >
                              Yes
                            </Button>
                          </DialogActions>
                        </Dialog>
                      </form>
                    </CardContent>
                  </Card>
                </div>
                <div className="mb-4">
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              justifyContent: "center",
                              textAlign: "center",
                            }}
                          >
                            ControlID
                          </TableCell>
                          <TableCell
                            sx={{
                              justifyContent: "center",
                              textAlign: "center",
                            }}
                          >
                            SubControlID
                          </TableCell>
                          <TableCell>SubControlName</TableCell>
                          <TableCell>QuestionID</TableCell>
                          <TableCell>Question</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {questions.map((question, index) => (
                          <TableRow key={index}>
                            <TableCell
                              sx={{
                                justifyContent: "center",
                                textAlign: "center",
                              }}
                            >
                              {question.ControlID}
                            </TableCell>
                            <TableCell
                              sx={{
                                justifyContent: "center",
                                textAlign: "center",
                              }}
                            >
                              {question.SubControlID || ""}
                            </TableCell>
                            <TableCell className="custom-wrap">
                              {question.SubControlName || ""}
                            </TableCell>
                            <TableCell>{question.Question_ID}</TableCell>
                            <TableCell className="custom-wrap">
                              {question.Question}
                            </TableCell>
                            {/* US-CAP-103: Question type badge */}
                            <TableCell>
                              <Chip
                                label={
                                  QUESTION_TYPE_LABELS[
                                    question.question_type
                                  ] || "Five Option"
                                }
                                size="small"
                                color={
                                  question.question_type &&
                                  question.question_type !== "five_option"
                                    ? "secondary"
                                    : "default"
                                }
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {question.status ? "Active" : "Inactive"}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() =>
                                  handleEditQuestionClick(question)
                                }
                              >
                                Edit
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={questions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </div>

                <Divider className="my-1" />
              </CardContent>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
export default ConfigureQuestions;
